import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { vendorsService } from '../../services/payables/vendors';
import { vendorPaymentsService } from '../../services/payables/vendor-payments';
import { useAuth } from '../../contexts/AuthContext';

const BillDetailModal = ({ bill, isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    if (isOpen && bill) {
      loadAdditionalData();
    }
  }, [isOpen, bill?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAdditionalData = async () => {
    setLoading(true);
    try {
      // Load vendor details
      if (bill.vendorId) {
        const vendorData = await vendorsService.getVendor(bill.vendorId);
        setVendor(vendorData);
      }

      // Load payment history
      const payments = await vendorPaymentsService.getVendorPayments(user.companyId);
      console.log('🔍 All payments loaded:', payments.length);
      const billPayments = payments.filter(payment => payment.billId === bill.id);
      console.log('🔍 Bill payments for bill', bill.id, ':', billPayments);
      setPaymentHistory(billPayments);
    } catch (error) {
      console.error('Error loading additional data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date?.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDaysOverdue = () => {
    if (!bill.dueDate) return 0;
    const dueDate = bill.dueDate?.toDate ? bill.dueDate.toDate() : new Date(bill.dueDate);
    const today = new Date();
    const diffTime = today - dueDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getTotalPaid = () => {
    return paymentHistory.reduce((total, payment) => total + (payment.amount || 0), 0);
  };

  const getOutstandingAmount = () => {
    return (bill.totalAmount || 0) - getTotalPaid();
  };

  if (!isOpen || !bill) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('bill_modals.billDetails')} - {bill.billNumber}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {vendor?.name || t('bill_payments.loadingVendor')}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bill.paymentStatus)}`}>
                {t(bill.paymentStatus)}
              </span>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('bill_modals.details')}
            </button>
            <button
              onClick={() => setActiveTab('lineItems')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'lineItems'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('bill_modals.lineItems')}
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'payments'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('bill_modals.paymentHistory')}
            </button>
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="space-y-6">
                  {/* Bill Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">{t('bill_modals.billInformation')}</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">{t('common.billNumber')}</label>
                          <p className="text-sm text-gray-900">{bill.billNumber}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">{t('bill_payments.billDate')}</label>
                          <p className="text-sm text-gray-900">{formatDate(bill.billDate)}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">{t('bill_payments.dueDate')}</label>
                          <p className="text-sm text-gray-900">{formatDate(bill.dueDate)}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">{t('accounting.table.status')}</label>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bill.status)}`}>
                            {t(bill.status)}
                          </span>
                        </div>
                        {getDaysOverdue() > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700">{t('bill_modals.daysOverdue')}</label>
                            <p className="text-sm text-red-600 font-medium">{getDaysOverdue()} {t('bill_modals.days')}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Vendor Information */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">{t('bill_modals.vendorInformation')}</h3>
                      {vendor ? (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">{t('bill_modals.vendorName')}</label>
                            <p className="text-sm text-gray-900">{vendor.name}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">{t('accounting.email')}</label>
                            <p className="text-sm text-gray-900">{vendor.email}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">{t('clients.phone')}</label>
                            <p className="text-sm text-gray-900">{vendor.phone}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">{t('clients.address')}</label>
                            <p className="text-sm text-gray-900">{vendor.address}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">{t('bill_modals.loadingVendorDetails')}</p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {bill.description && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{t('accounting.description')}</h3>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">{bill.description}</p>
                    </div>
                  )}

                  {/* Financial Summary */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">{t('bill_modals.financialSummary')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">{t('accounting.subtotal')}:</span>
                          <span className="text-sm font-medium">{formatCurrency(bill.subtotal)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">{t('bills.tax')}:</span>
                          <span className="text-sm font-medium">{formatCurrency(bill.taxAmount)}</span>
                        </div>
                        <div className="flex justify-between border-t pt-2">
                          <span className="text-sm font-medium">{t('accounting.total')}:</span>
                          <span className="text-sm font-bold">{formatCurrency(bill.totalAmount)}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">{t('bill_payments.totalPaid')}:</span>
                          <span className="text-sm font-medium text-green-600">{formatCurrency(getTotalPaid())}</span>
                        </div>
                        <div className="flex justify-between border-t pt-2">
                          <span className="text-sm font-medium">{t('bill_modals.outstanding')}:</span>
                          <span className="text-sm font-bold text-red-600">{formatCurrency(getOutstandingAmount())}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Line Items Tab */}
              {activeTab === 'lineItems' && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">{t('bill_modals.lineItems')}</h3>
                  {bill.lineItems && bill.lineItems.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('accounting.description')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('accounting.quantity')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('common.unitPrice')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('accounting.total')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {bill.lineItems.map((item, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.description}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.quantity}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(item.unitPrice)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCurrency(item.total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">{t('bill_modals.noLineItemsFound')}</p>
                  )}
                </div>
              )}

              {/* Payments Tab */}
              {activeTab === 'payments' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">{t('bill_modals.paymentHistory')}</h3>
                    <button
                      onClick={loadAdditionalData}
                      disabled={loading}
                      className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                    >
                      {loading ? t('common.loading') : t('common.refresh')}
                    </button>
                  </div>
                  {paymentHistory.length > 0 ? (
                    <div className="space-y-4">
                      {paymentHistory.map((payment, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {formatCurrency(payment.amount)}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(payment.status)}`}>
                                  {t(`bill_payments.${payment.status}`) || t(payment.status) || payment.status}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                <p>{t('bill_payments.paymentDate')}: {formatDate(payment.paymentDate)}</p>
                                <p>{t('bill_payments.paymentMethod')}: {t(`bill_payments.${payment.paymentMethod}`) || t(payment.paymentMethod) || payment.paymentMethod}</p>
                                {payment.description && (
                                  <p>{t('accounting.description')}: {payment.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">{t('bill_modals.noPaymentsFound')}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-6 border-t">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {t('bill_modals.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillDetailModal; 